/**
 * Registry central de tours.
 *
 * Cada tour declara sus steps con selectores `[data-tour="..."]` sobre
 * elementos reales del app. Para agregar un tour:
 *   1. Añade `data-tour="xxx"` a los elementos HTML que quieres destacar
 *   2. Agrega el tour acá con sus steps
 *   3. Si quieres auto-trigger en primera visita, incluye la ruta en `autoTriggerOn`
 *
 * El texto debe estar en español neutro (sin voseo argentino).
 */
import type { TourRegistry } from './types'

export const TOUR_REGISTRY: TourRegistry = {
  app: {
    id: 'app',
    label: 'Bienvenido a Clobi',
    description: 'Recorrido por las funciones principales de la plataforma.',
    autoTriggerOn: ['/dashboard'],
    steps: [
      {
        target: '[data-tour="app-logo"]',
        title: '¡Hola 👋 bienvenido a Clobi!',
        content:
          'Un recorrido de un minuto por las partes más importantes de la plataforma. Se puede saltar en cualquier momento y retomar desde el menú de ayuda.',
        placement: 'bottom-start',
      },
      {
        target: '[data-tour="sidebar"]',
        title: 'Menú lateral',
        content:
          'Desde aquí accedes a todos los módulos: Ventas, Compras, Contabilidad, Inventario, Cobranza, etc. Los elementos visibles dependen de los permisos que te asignó tu administrador.',
        placement: 'right',
      },
      {
        target: '[data-tour="dashboard"]',
        title: 'Dashboard',
        content:
          'Tu pantalla de inicio con los indicadores clave del negocio: ventas del mes, cobranza vencida, flujo de caja y alertas importantes.',
        placement: 'center',
      },
      {
        target: '[data-tour="theme-toggle"]',
        title: 'Modo claro u oscuro',
        content:
          'Permite elegir el tema: claro, oscuro o seguir la configuración del sistema operativo. La elección queda guardada.',
        placement: 'bottom',
      },
      {
        target: '[data-tour="updates-trigger"]',
        title: 'Novedades de Clobi',
        content:
          'Aquí te informamos sobre nuevas funcionalidades, mejoras y correcciones de la plataforma. Revísalo de vez en cuando para no perderte nada.',
        placement: 'bottom',
      },
      {
        target: '[data-tour="notification-bell"]',
        title: 'Notificaciones',
        content:
          'Alertas de la empresa: documentos aceptados o rechazados por el SII, facturas vencidas, pagos registrados, entre otras. Se configuran desde el perfil.',
        placement: 'bottom',
      },
      {
        target: '[data-tour="user-menu"]',
        title: 'Tu perfil',
        content:
          'Desde acá se cambia la contraseña, se ajustan las preferencias de notificaciones, se elige el idioma y se cierra sesión. Quien pertenece a más de una empresa también puede cambiar de empresa desde este menú.',
        placement: 'bottom-end',
      },
      {
        target: '[data-tour="help-panel"]',
        title: 'Ayuda y recorridos',
        content:
          'Este botón está en cada pantalla. Además de la ayuda contextual, desde acá se puede volver a ejecutar este recorrido o el específico de cada módulo cuando haga falta.',
        placement: 'left',
      },
    ],
  },

  // ─── Ventas ──────────────────────────────────────────────────────────────
  'sales.documents': {
    id: 'sales.documents',
    label: 'Documentos de Venta',
    description: 'Cómo emitir, filtrar y gestionar tus facturas y boletas.',
    autoTriggerOn: ['/sales/documents'],
    steps: [
      {
        target: '[data-tour="sales-docs-page"]',
        title: 'Documentos de Venta',
        content:
          'En este módulo emites y gestionas tus facturas, boletas, notas de crédito y débito. Cada documento se firma y envía al SII automáticamente.',
        placement: 'center',
      },
      {
        target: '[data-tour="sales-docs-new"]',
        title: 'Nuevo documento',
        content:
          'Crea una factura, boleta u otro tipo de documento: se selecciona el cliente, se agregan productos o servicios y el sistema calcula los impuestos.',
        placement: 'bottom-end',
      },
      {
        target: '[data-tour="sales-docs-filters"]',
        title: 'Filtros',
        content:
          'Filtra por estado, tipo de documento, fecha, cliente o número de folio. La búsqueda es rápida sobre todo el catálogo de documentos.',
        placement: 'bottom',
      },
      {
        target: '[data-tour="sales-docs-table"]',
        title: 'Tabla de documentos',
        content:
          'Cada fila muestra el estado del documento (borrador, enviado al SII, aceptado, rechazado, anulado), el folio, el cliente y el total. Al hacer clic sobre una fila se ve el detalle.',
        placement: 'top',
      },
    ],
  },

  // ─── Compras ─────────────────────────────────────────────────────────────
  'purchases.invoices': {
    id: 'purchases.invoices',
    label: 'Facturas de Proveedor',
    description: 'Cómo registrar y gestionar facturas recibidas.',
    autoTriggerOn: ['/purchases/invoices'],
    steps: [
      {
        target: '[data-tour="purchases-page"]',
        title: 'Facturas de Proveedor',
        content:
          'Acá se registran todas las facturas recibidas de los proveedores. Se pueden cargar desde el RCV del SII, subir el XML, o ingresarlas manualmente.',
        placement: 'center',
      },
      {
        target: '[data-tour="purchases-rcv"]',
        title: 'Descargar RCV',
        content:
          'Importa automáticamente todas las facturas que el SII tiene registradas a tu nombre. Solo aplica para empresas chilenas con certificado digital cargado.',
        placement: 'bottom',
      },
      {
        target: '[data-tour="purchases-xml"]',
        title: 'Cargar XML',
        content:
          'Si recibiste el XML de un DTE específico, súbelo aquí y se registra como factura. El sistema valida la firma y los datos.',
        placement: 'bottom',
      },
      {
        target: '[data-tour="purchases-new"]',
        title: 'Nueva factura manual',
        content:
          'Para proveedores que no emiten DTE (por ejemplo internacionales), registra la factura manualmente. Útil también para gastos pequeños.',
        placement: 'bottom-end',
      },
      {
        target: '[data-tour="purchases-filters"]',
        title: 'Filtros',
        content:
          'Filtra por estado, origen (RCV, XML, manual), estado contable, fecha de recepción y más. Marca facturas como huérfanas si tienen NC/ND sin referencia.',
        placement: 'bottom',
      },
    ],
  },

  // ─── Cobranza ────────────────────────────────────────────────────────────
  'receivables.aging': {
    id: 'receivables.aging',
    label: 'Antigüedad de Saldos (Cobranza)',
    description: 'Cómo monitorear las cuentas por cobrar.',
    autoTriggerOn: ['/receivables/aging'],
    steps: [
      {
        target: '[data-tour="aging-page"]',
        title: 'Antigüedad de Saldos',
        content:
          'Esta pantalla muestra cuánto te debe cada cliente clasificado por días de mora. Es la herramienta principal para gestionar la cobranza.',
        placement: 'center',
      },
      {
        target: '[data-tour="aging-summary"]',
        title: 'Resumen por buckets',
        content:
          'Vigente (sin vencer), 1-30, 31-60, 61-90 y más de 90 días. El total de cuentas por cobrar a la derecha.',
        placement: 'bottom',
      },
      {
        target: '[data-tour="aging-export"]',
        title: 'Exportar a CSV',
        content:
          'Descarga el detalle completo en CSV para llevarlo a Excel o compartirlo con tu equipo de cobranza.',
        placement: 'left',
      },
      {
        target: '[data-tour="aging-table"]',
        title: 'Detalle por cliente',
        content:
          'Cada fila muestra los saldos del cliente distribuidos en los buckets. Lo que aparece en rojo son saldos con más de 90 días de mora — máxima prioridad.',
        placement: 'top',
      },
    ],
  },

  // ─── Contabilidad ────────────────────────────────────────────────────────
  'accounting.vouchers': {
    id: 'accounting.vouchers',
    label: 'Comprobantes Contables',
    description: 'Cómo crear, aprobar y exportar asientos contables.',
    autoTriggerOn: ['/accounting/vouchers'],
    steps: [
      {
        target: '[data-tour="vouchers-page"]',
        title: 'Comprobantes Contables',
        content:
          'Los asientos contables (vouchers) son el registro de todas las operaciones financieras. Algunos se generan automáticamente desde ventas, compras y pagos; otros los creas manualmente.',
        placement: 'center',
      },
      {
        target: '[data-tour="vouchers-new"]',
        title: 'Nuevo comprobante',
        content:
          'Crea un asiento manual para casos no cubiertos por la automatización: ajustes, provisiones, depreciaciones, etc.',
        placement: 'bottom-end',
      },
      {
        target: '[data-tour="vouchers-filters"]',
        title: 'Filtros',
        content:
          'Filtra por estado (borrador, aprobado, rechazado), tipo, fecha, número de comprobante o origen. Útil para auditorías o cierres mensuales.',
        placement: 'bottom',
      },
      {
        target: '[data-tour="vouchers-table"]',
        title: 'Tabla de comprobantes',
        content:
          'Cada fila muestra el número, fecha, descripción, montos del Debe y Haber, y el estado. Al hacer clic en una fila se puede ver el detalle, aprobar, rechazar, revertir o descargar el PDF.',
        placement: 'top',
      },
    ],
  },

  // ─── Productos (catálogo) ────────────────────────────────────────────────
  'inventory.products': {
    id: 'inventory.products',
    label: 'Productos',
    description: 'Cómo gestionar tu catálogo de productos y servicios.',
    autoTriggerOn: ['/sales/products'],
    steps: [
      {
        target: '[data-tour="products-page"]',
        title: 'Catálogo de Productos',
        content:
          'Tu inventario completo de productos y servicios. Cada uno con SKU, precio, moneda, stock (si aplica) y cuentas contables asignadas.',
        placement: 'center',
      },
      {
        target: '[data-tour="products-new"]',
        title: 'Nuevo producto',
        content:
          'Agrega un producto o servicio. Un producto físico permite llevar control de stock; un servicio, no.',
        placement: 'bottom-end',
      },
      {
        target: '[data-tour="products-import"]',
        title: 'Importar CSV',
        content:
          'Carga masiva desde un Excel o CSV. Soporta el formato de Zoho Books para migraciones. El sistema mapea las columnas automáticamente.',
        placement: 'bottom',
      },
      {
        target: '[data-tour="products-table"]',
        title: 'Tabla de productos',
        content:
          'Cada fila muestra SKU, nombre, precio, moneda, stock y estado. Al hacer clic se puede editar, ajustar stock o desactivar.',
        placement: 'top',
      },
    ],
  },

  // ─── Formulario de Documento de Venta ────────────────────────────────────
  'sales.documents.new': {
    id: 'sales.documents.new',
    label: 'Emisión de Documento',
    description: 'Cómo completar el formulario de emisión de un documento de venta.',
    autoTriggerOn: ['/sales/documents/new'],
    steps: [
      {
        target: '[data-tour="docform-page"]',
        title: 'Emisión de Documento',
        content:
          'Esta es la pantalla más completa del módulo de Ventas. Desde aquí emites una factura, boleta, nota de crédito u otro tipo de documento. Te guiamos por las secciones principales.',
        placement: 'center',
      },
      {
        target: '[data-tour="docform-sidebar"]',
        title: 'Navegación lateral',
        content:
          'Las secciones del documento están organizadas en este menú lateral. Permite saltar de una a otra rápidamente. Los campos obligatorios aparecen marcados con un asterisco rojo.',
        placement: 'left',
      },
      {
        target: '[data-tour="docform-doctype"]',
        title: 'Tipo y datos del documento',
        content:
          'Acá se elige el tipo de documento (factura, boleta, etc.). Según el tipo, los campos disponibles cambian automáticamente conforme al esquema del SII.',
        placement: 'right',
      },
      {
        target: '[data-tour="docform-customer"]',
        title: 'Receptor',
        content:
          'El cliente se busca por RUT o nombre. Si está registrado, sus datos se completan solos. También se puede crear un cliente nuevo desde el módulo de Clientes y volver acá.',
        placement: 'right',
      },
      {
        target: '[data-tour="docform-lines"]',
        title: 'Líneas de detalle',
        content:
          'Agrega los productos o servicios. Por cada línea: cantidad, precio, descuento opcional y código. Los totales (subtotal, IVA, total) se calculan automáticamente.',
        placement: 'top',
      },
      {
        target: '[data-tour="docform-help"]',
        title: 'Ayuda contextual',
        content:
          'Si te quedas con dudas en algún campo, abre este botón. Tendrás explicaciones detalladas de cada sección y la lista de tours disponibles.',
        placement: 'bottom-end',
      },
    ],
  },

  // ─── Formulario de Pedido de Venta ───────────────────────────────────────
  'sales.orders.new': {
    id: 'sales.orders.new',
    label: 'Pedido de Venta',
    description: 'Cómo crear un pedido y convertirlo después a documento.',
    autoTriggerOn: ['/sales/orders/new'],
    steps: [
      {
        target: '[data-tour="orderform-page"]',
        title: 'Nuevo pedido de venta',
        content:
          'Los pedidos son una etapa previa a la emisión: te permiten cotizar, aprobar internamente y luego convertir a factura o boleta. Útil cuando el cliente pide formalizar antes de emitir.',
        placement: 'center',
      },
      {
        target: '[data-tour="orderform-customer"]',
        title: 'Cliente',
        content:
          'Acá se selecciona o crea el cliente. A diferencia del documento de venta, el RUT acá puede estar incompleto — un pedido se puede aprobar antes de tener todos los datos del cliente.',
        placement: 'right',
      },
      {
        target: '[data-tour="orderform-lines"]',
        title: 'Líneas del pedido',
        content:
          'Productos o servicios cotizados. Se puede agregar descripción ampliada por línea, asignar un vendedor para comisiones y dejar líneas como referencia sin tocar inventario.',
        placement: 'top',
      },
      {
        target: '[data-tour="orderform-payment"]',
        title: 'Condiciones de pago',
        content:
          'Forma de pago, plazo y observaciones. Estos datos se heredan al documento de venta cuando el pedido se convierte. Se pueden editar al momento de emitir.',
        placement: 'top',
      },
    ],
  },

  // ─── Config: Facturador ──────────────────────────────────────────────────
  'config.invoicer': {
    id: 'config.invoicer',
    label: 'Facturador Electrónico',
    description: 'Cómo configurar el proveedor de facturación y el ambiente SII.',
    autoTriggerOn: ['/config/invoicer'],
    steps: [
      {
        target: '[data-tour="invoicer-page"]',
        title: 'Facturador Electrónico',
        content:
          'Esta es una de las pantallas más críticas del sistema: aquí defines el proveedor que se encarga de firmar y enviar tus DTEs al SII. Una mala configuración puede impedir la emisión.',
        placement: 'center',
      },
      {
        target: '[data-tour="invoicer-environment"]',
        title: 'Ambiente SII',
        content:
          'Homologación es para pruebas — los DTEs no son válidos tributariamente. Producción envía al SII real. Solo cambia a Producción cuando estés seguro que todo funciona en homologación.',
        placement: 'right',
      },
      {
        target: '[data-tour="invoicer-active"]',
        title: 'Estado del facturador',
        content:
          'Si está apagado, los DTEs se generan pero no se envían al SII. Útil para revisar borradores o si tu proveedor está en mantenimiento.',
        placement: 'left',
      },
    ],
  },

  // ─── Config: Tipos DTE ───────────────────────────────────────────────────
  'config.dte-types': {
    id: 'config.dte-types',
    label: 'Tipos de Documento',
    description: 'Cómo activar tipos de DTE y configurar campos del esquema SII.',
    autoTriggerOn: ['/config/dte-types'],
    steps: [
      {
        target: '[data-tour="dte-types-page"]',
        title: 'Tipos de Documento',
        content:
          'Acá se controla qué tipos de documento (factura, boleta, NC, ND, etc.) puede emitir la empresa, y qué campos del esquema SII se muestran en el formulario de emisión.',
        placement: 'center',
      },
      {
        target: '[data-tour="dte-types-list"]',
        title: 'Lista de tipos',
        content:
          'Cada tipo tiene un toggle para activarlo o desactivarlo. Si lo desactivas, ya no aparece como opción al emitir un documento.',
        placement: 'right',
      },
      {
        target: '[data-tour="dte-types-fields"]',
        title: 'Campos del documento',
        content:
          'Al seleccionar un tipo se ven los campos del esquema SII agrupados por zona (Encabezado, Receptor, Detalle, etc.). Los obligatorios (rojos) no se pueden desactivar; los opcionales sí.',
        placement: 'left',
      },
    ],
  },

  // ─── Config: Notificaciones ──────────────────────────────────────────────
  'config.notifications': {
    id: 'config.notifications',
    label: 'Notificaciones de la Empresa',
    description: 'Cómo configurar los canales y eventos de notificación de la empresa.',
    autoTriggerOn: ['/config/notifications'],
    steps: [
      {
        target: '[data-tour="notif-config-page"]',
        title: 'Configuración de Notificaciones',
        content:
          'Define qué eventos del sistema se notifican y por qué canal. Acá se establecen los valores por defecto de la empresa — cada usuario después puede personalizar los suyos en su perfil.',
        placement: 'center',
      },
      {
        target: '[data-tour="notif-config-matrix"]',
        title: 'Matriz de eventos × canales',
        content:
          'Cada fila es un evento (factura aceptada, pago vencido, etc.). Las columnas son los canales: in-app, email, push y canales externos (Slack, Teams, Google Chat).',
        placement: 'top',
      },
      {
        target: '[data-tour="notif-config-forced"]',
        title: 'Canales obligatorios',
        content:
          'Los canales marcados como obligatorios (candado) no pueden ser desactivados por los usuarios — útil para alertas críticas que no deben perderse.',
        placement: 'top',
      },
      {
        target: '[data-tour="notif-config-external"]',
        title: 'Canales externos',
        content:
          'Configura webhooks de Slack, Microsoft Teams o Google Chat. Admite múltiples canales por evento (por ejemplo, notificar pedidos a `#ventas` y `#bodega` a la vez).',
        placement: 'top',
      },
    ],
  },

  // ─── Reportes ────────────────────────────────────────────────────────────
  reports: {
    id: 'reports',
    label: 'Centro de Reportes',
    description: 'Cómo acceder a reportes prearmados y personalizados.',
    autoTriggerOn: ['/reports'],
    steps: [
      {
        target: '[data-tour="reports-page"]',
        title: 'Centro de Reportes',
        content:
          'Acceso a los reportes principales del negocio: ventas, compras, antigüedad de saldos, F29, top clientes, factoring y más.',
        placement: 'center',
      },
      {
        target: '[data-tour="reports-custom"]',
        title: 'Reportes Personalizados',
        content:
          'Genera tu propio reporte eligiendo entidad, campos y filtros. Exporta a Excel o CSV. Si tu reporte tiene más de 5.000 filas se procesa en segundo plano y te llega un correo con el link.',
        placement: 'bottom',
      },
      {
        target: '[data-tour="reports-hub"]',
        title: 'Reportes prearmados',
        content:
          'Cada tarjeta abre un reporte específico con tabla, filtros y exportación a Excel. Ideal para análisis recurrentes.',
        placement: 'top',
      },
    ],
  },
}
